import { useState, useEffect } from 'react';
import { api, pfmt } from '../../lib/api';
import { TID } from '../../constants/testIds';
import { toast } from 'sonner';
import { Bell, BellRing } from 'lucide-react';

const STORAGE_KEY = 'bn_watch_email';

export default function WatchlistPanel({ neighbourhood }) {
    const [email, setEmail] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) || '' : ''));
    const [threshold, setThreshold] = useState(2);
    const [busy, setBusy] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [preview, setPreview] = useState(null);

    // If user already has this slug in their watchlist, show it
    useEffect(() => {
        let cancel = false;
        async function load() {
            if (!email) return;
            try {
                const { data } = await api.get('/watchlist/status', { params: { email } });
                if (cancel) return;
                const mine = (data.items || []).find((i) => i.slug === neighbourhood.slug);
                if (mine) {
                    setPreview(mine);
                    setConfirmed(true);
                }
            } catch (_e) { /* ignore */ }
        }
        load();
        return () => { cancel = true; };
    }, [email, neighbourhood.slug]);

    async function subscribe(e) {
        e.preventDefault();
        if (!email || busy) return;
        setBusy(true);
        try {
            await api.post('/watchlist/subscribe', {
                email,
                slug: neighbourhood.slug,
                threshold_pct: Number(threshold),
                alert_types: ['price_change', 'forecast_change', '1yr_change'],
            });
            localStorage.setItem(STORAGE_KEY, email);
            setConfirmed(true);
            toast.success(`We'll notify you when ${neighbourhood.name} moves.`);
            // reload status
            const { data } = await api.get('/watchlist/status', { params: { email } });
            const mine = (data.items || []).find((i) => i.slug === neighbourhood.slug);
            if (mine) setPreview(mine);
        } catch (_e) {
            toast.error('Could not set your alert. Try again.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <section data-testid={TID.watch.panel} className="border-t border-b border-neutral-200 bg-[#F7F7F5]">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5">
                    <div className="flex items-center gap-2 overline">
                        <Bell className="w-3.5 h-3.5" />
                        Watchlist alert
                    </div>
                    <h3 className="font-editorial text-[30px] lg:text-[38px] tracking-editorial mt-4 leading-[1.08]">
                        Be notified when {neighbourhood.name} moves.
                    </h3>
                    <p className="mt-4 text-[14.5px] text-neutral-600 max-w-md leading-relaxed">
                        You&apos;ll be notified when the median price, 1-year change or 5-year forecast shifts beyond your threshold. No listings. No spam.
                    </p>
                </div>

                <div className="lg:col-span-7">
                    {confirmed && preview ? (
                        <div data-testid={TID.watch.confirmed} className="border border-neutral-300 bg-white p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="flex items-center gap-2 overline text-[#2F6B47]">
                                        <BellRing className="w-3.5 h-3.5" />
                                        Alert active
                                    </div>
                                    <div className="font-editorial text-[22px] mt-2 tracking-editorial">
                                        Watching {neighbourhood.name}
                                    </div>
                                    <div className="text-[13px] text-neutral-500 mt-1 font-mono-data">
                                        {email} · threshold ±{preview.threshold_pct}%
                                    </div>
                                </div>
                                {preview.has_alert ? (
                                    <span data-testid={TID.watch.alertPill} className="text-[11px] uppercase tracking-[0.18em] px-2.5 py-1 border border-[#2F6B47] text-[#2F6B47] bg-white">
                                        {preview.triggered.length} pending
                                    </span>
                                ) : (
                                    <span className="text-[11px] uppercase tracking-[0.18em] px-2.5 py-1 border border-neutral-300 text-neutral-500">
                                        Stable
                                    </span>
                                )}
                            </div>

                            <div className="mt-6 grid grid-cols-3 gap-0 border-l border-t border-neutral-200">
                                <DeltaCell label="Price" delta={preview.price_delta_pct} unit="%" />
                                <DeltaCell label="1Y change" delta={preview.y1_delta_pts} unit="pp" />
                                <DeltaCell label="5Y forecast" delta={preview.forecast_delta_pts} unit="pp" />
                            </div>

                            <div className="mt-4 text-[11px] uppercase tracking-[0.14em] text-neutral-400 font-mono-data">
                                Baseline captured {preview.baseline.captured_at?.slice(0, 10)}
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={subscribe} className="border border-neutral-300 bg-white p-6">
                            <label className="overline">Your email</label>
                            <input
                                data-testid={TID.watch.email}
                                type="email"
                                required
                                placeholder="you@company.ca"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-2 w-full border border-neutral-300 px-3 py-2.5 text-[15px] bg-white focus:border-black outline-none"
                               
                            />

                            <label className="overline mt-6 block">Notify me when any metric moves by</label>
                            <div className="mt-3 flex items-center gap-4">
                                <input
                                    data-testid={TID.watch.threshold}
                                    type="range"
                                    min={1} max={10} step={0.5}
                                    value={threshold}
                                    onChange={(e) => setThreshold(Number(e.target.value))}
                                    className="flex-1"
                                />
                                <div className="font-mono-data text-[18px] tabular w-16 text-right">±{pfmt(threshold, 1)}</div>
                            </div>

                            <button
                                data-testid={TID.watch.submit}
                                type="submit"
                                disabled={busy}
                                className="mt-6 inline-flex items-center gap-2 bg-brand-navy text-white px-6 py-3 text-[13.5px] hover-invert hover:bg-brand-gold hover:text-brand-navy border border-brand-navy disabled:opacity-50"
                               
                            >
                                <Bell className="w-4 h-4" />
                                {busy ? 'Setting alert…' : `Alert me if ${neighbourhood.name} moves`}
                            </button>
                            <div className="mt-3 text-[11px] uppercase tracking-[0.14em] text-neutral-400 font-mono-data">
                                Free · Unsubscribe anytime
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}

function DeltaCell({ label, delta, unit }) {
    const positive = delta > 0;
    const zero = delta === 0;
    const color = zero ? 'text-neutral-500' : positive ? 'text-[#0B8A3E]' : 'text-[#C3341C]';
    const sign = positive ? '+' : '';
    return (
        <div className="border-r border-b border-neutral-200 p-4">
            <div className="overline">{label}</div>
            <div className={`font-mono-data text-[20px] tabular mt-1 ${color}`}>
                {zero ? '—' : `${sign}${delta.toFixed(2)}${unit}`}
            </div>
        </div>
    );
}
